import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { GeminiHeader } from "@/components/GeminiHeader";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import * as LucideIcons from "lucide-react";
import { 
  Search, Info, ChevronRight, Sparkles, Users, Shield, 
  BarChart3, Brain, Network, ClipboardCheck, MapPin, 
  Zap, BookOpen, HelpCircle, AlertTriangle, Play,
  Mic, GraduationCap, FileText, Settings, Star
} from "lucide-react";

interface HelpCategory {
  id: string;
  name: string;
  name_id: string;
  icon: string;
  target_roles: string[];
  sort_order: number;
}

interface HelpArticle {
  id: string;
  slug: string;
  title: string;
  title_id: string;
  summary: string;
  summary_id: string;
  target_roles: string[];
  is_featured: boolean;
  category_id: string;
  has_video?: boolean;
}

// Premium role color mapping with gradients
const roleConfig: Record<string, { 
  gradient: string; 
  text: string; 
  border: string;
  glow: string;
  icon: React.ReactNode;
  label: string;
}> = {
  worker: { 
    gradient: "from-blue-500/20 via-blue-400/10 to-cyan-500/20", 
    text: "text-blue-600 dark:text-blue-400", 
    border: "border-blue-500/30",
    glow: "shadow-blue-500/20",
    icon: <Users className="h-3.5 w-3.5" />,
    label: "Pekerja"
  },
  supervisor: { 
    gradient: "from-emerald-500/20 via-green-400/10 to-teal-500/20", 
    text: "text-emerald-600 dark:text-emerald-400", 
    border: "border-emerald-500/30",
    glow: "shadow-emerald-500/20",
    icon: <Shield className="h-3.5 w-3.5" />,
    label: "Supervisor"
  },
  admin: { 
    gradient: "from-purple-500/20 via-violet-400/10 to-indigo-500/20", 
    text: "text-purple-600 dark:text-purple-400", 
    border: "border-purple-500/30",
    glow: "shadow-purple-500/20",
    icon: <BarChart3 className="h-3.5 w-3.5" />,
    label: "Admin"
  },
};

// Decorative illustrations for categories
const categoryIllustrations: Record<string, string> = {
  "getting-started": "🚀",
  "hazard-reporting": "⚠️",
  "safety-checkin": "✅",
  "quests": "🎯",
  "inspections": "📋",
  "learning": "📚",
  "supervisor": "👷",
  "admin": "📊",
};

const HelpCenter = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<HelpArticle[]>([]);
  const [userRole, setUserRole] = useState<string>("worker");
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string | null>(null);

  useEffect(() => {
    loadHelpContent();
  }, []);

  const loadHelpContent = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile } = await supabase
          .from("worker_profiles")
          .select("role")
          .eq("user_id", session.user.id)
          .maybeSingle();
        
        if (profile?.role) {
          setUserRole(profile.role);
        }

        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .maybeSingle();
        
        if (roleData?.role === "admin" || roleData?.role === "super_admin" || roleData?.role === "site_admin") {
          setUserRole("admin");
        }
      }

      const { data: categoriesData } = await supabase
        .from("help_categories")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      
      if (categoriesData) {
        setCategories(categoriesData);
      }

      const { data: articlesData } = await supabase
        .from("help_articles")
        .select("*")
        .eq("is_active", true)
        .eq("is_featured", true)
        .order("sort_order");
      
      if (articlesData) {
        setFeaturedArticles(articlesData);
      }
    } catch (error) {
      console.error("Error loading help content:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCategories = selectedRoleFilter
    ? categories.filter(cat => cat.target_roles.includes(selectedRoleFilter))
    : categories;

  const searchedCategories = searchQuery.trim() 
    ? filteredCategories.filter(cat => {
        const name = language === "id" ? cat.name_id : cat.name;
        return name.toLowerCase().includes(searchQuery.toLowerCase());
      })
    : filteredCategories;

  const getCategoryRole = (cat: HelpCategory): string => {
    if (cat.target_roles.includes("admin")) return "admin";
    if (cat.target_roles.includes("supervisor")) return "supervisor";
    return "worker";
  };

  const aboutSorinArticle = featuredArticles.find(a => a.slug === "what-is-sorin");

  const getIcon = (iconName: string) => {
    return (LucideIcons as any)[iconName] || HelpCircle;
  };

  const getIllustration = (catName: string): string => {
    const key = catName.toLowerCase().replace(/\s+/g, '-');
    return categoryIllustrations[key] || "📖";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      <GeminiHeader title={language === "id" ? "Pusat Bantuan" : "Help Center"} showBack />
      
      <div className="p-4 pb-24 max-w-2xl mx-auto">
        {/* Premium Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl mb-6"
        >
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-accent/20 to-primary/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,hsl(var(--accent)/0.2),transparent_50%)]" />
          
          {/* Floating decorative elements */}
          <motion.div 
            className="absolute top-4 right-8 w-20 h-20 rounded-full bg-primary/10 blur-2xl"
            animate={{ y: [0, -10, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-4 left-8 w-16 h-16 rounded-full bg-accent/10 blur-xl"
            animate={{ y: [0, 10, 0], scale: [1.1, 1, 1.1] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          
          <div className="relative z-10 p-6 backdrop-blur-[2px]">
            <div className="flex items-start gap-4 mb-5">
              <motion.div 
                className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 border border-primary/20 flex items-center justify-center shadow-lg shadow-primary/10"
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <GraduationCap className="h-8 w-8 text-primary" />
              </motion.div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">
                  {language === "id" ? "Ada yang bisa dibantu?" : "How can we help?"}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {language === "id" ? "Panduan lengkap untuk menggunakan SORIN" : "Complete guide to using SORIN"}
                </p>
              </div>
            </div>

            {/* Glass search bar */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-2xl blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity" />
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  placeholder={language === "id" ? "Cari topik bantuan..." : "Search help topics..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 pl-12 pr-12 rounded-2xl border-white/20 bg-background/80 backdrop-blur-xl shadow-lg text-base"
                />
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
                >
                  <Mic className="h-4 w-4 text-primary" />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Role Filter Pills - Premium style */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedRoleFilter(null)}
            className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              selectedRoleFilter === null
                ? "bg-foreground text-background shadow-lg"
                : "bg-muted/50 text-muted-foreground hover:bg-muted"
            }`}
          >
            {language === "id" ? "Semua" : "All"}
          </motion.button>
          
          {Object.entries(roleConfig).map(([role, config]) => (
            <motion.button
              key={role}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedRoleFilter(selectedRoleFilter === role ? null : role)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
                selectedRoleFilter === role
                  ? `bg-gradient-to-r ${config.gradient} ${config.text} ${config.border} shadow-lg ${config.glow}`
                  : `bg-gradient-to-r ${config.gradient} ${config.text} ${config.border} opacity-70 hover:opacity-100`
              }`}
            >
              {config.icon}
              {config.label}
            </motion.button>
          ))}
        </motion.div>

        {/* Featured: What is SORIN? - Premium card */}
        {aboutSorinArticle && !searchQuery && !selectedRoleFilter && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <motion.button
              onClick={() => navigate(`/help/${aboutSorinArticle.slug}`)}
              className="w-full relative overflow-hidden rounded-3xl text-left group"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {/* Premium gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/5 to-accent/20" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_0%,hsl(var(--primary)/0.2),transparent_60%)]" />
              
              {/* Animated border glow */}
              <div className="absolute inset-0 rounded-3xl border border-primary/30 group-hover:border-primary/50 transition-colors" />
              
              {/* Decorative play button */}
              <motion.div 
                className="absolute top-6 right-6 h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
              >
                <Play className="h-5 w-5 text-primary fill-primary" />
              </motion.div>
              
              <div className="relative z-10 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
                    <Star className="h-3 w-3 mr-1" />
                    {language === "id" ? "Wajib Dibaca" : "Must Read"}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center text-4xl">
                    🤖
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-xl mb-1 group-hover:text-primary transition-colors">
                      {language === "id" ? aboutSorinArticle.title_id : aboutSorinArticle.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {language === "id" ? aboutSorinArticle.summary_id : aboutSorinArticle.summary}
                    </p>
                  </div>
                  <ChevronRight className="h-6 w-6 text-muted-foreground group-hover:text-primary group-hover:translate-x-2 transition-all" />
                </div>
              </div>
            </motion.button>
          </motion.div>
        )}

        {/* Categories Grid - Premium masonry style */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            {language === "id" ? "Topik Bantuan" : "Help Topics"}
          </h2>
          
          {isLoading ? (
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Skeleton key={i} className="h-36 rounded-2xl" />
              ))}
            </div>
          ) : searchedCategories.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16 bg-gradient-to-br from-muted/50 to-muted/30 rounded-3xl border border-border/50"
            >
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-lg font-medium text-muted-foreground">
                {language === "id" ? "Tidak ada hasil" : "No results found"}
              </p>
              <p className="text-sm text-muted-foreground/60 mt-1">
                {language === "id" ? "Coba kata kunci lain" : "Try different keywords"}
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {searchedCategories.map((category, index) => {
                const Icon = getIcon(category.icon);
                const role = getCategoryRole(category);
                const config = roleConfig[role];
                const illustration = getIllustration(category.name);
                
                return (
                  <motion.button
                    key={category.id}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: index * 0.05, type: "spring", stiffness: 300 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate(`/help/category/${category.id}`)}
                    className={`relative flex flex-col items-start gap-3 p-4 h-36 rounded-2xl border transition-all overflow-hidden group bg-gradient-to-br ${config.gradient} ${config.border} hover:shadow-lg ${config.glow}`}
                  >
                    {/* Decorative illustration */}
                    <motion.div 
                      className="absolute -right-2 -bottom-2 text-5xl opacity-20 group-hover:opacity-40 transition-opacity"
                      animate={{ rotate: [0, 5, 0] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      {illustration}
                    </motion.div>
                    
                    {/* Role badge */}
                    <div className={`absolute top-3 right-3 h-6 w-6 rounded-full flex items-center justify-center ${config.text} bg-background/80 backdrop-blur-sm text-xs font-bold border ${config.border}`}>
                      {role === "worker" ? "W" : role === "supervisor" ? "S" : "A"}
                    </div>
                    
                    <div className={`h-11 w-11 rounded-xl bg-background/80 backdrop-blur-sm border ${config.border} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className={`h-5 w-5 ${config.text}`} />
                    </div>
                    
                    <div className="flex-1 text-left relative z-10">
                      <span className="text-sm font-semibold line-clamp-2 leading-tight group-hover:text-foreground transition-colors">
                        {language === "id" ? category.name_id : category.name}
                      </span>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>

        {/* Featured Articles - Premium list */}
        {featuredArticles.length > 0 && !searchQuery && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
              <Zap className="h-4 w-4" />
              {language === "id" ? "Artikel Populer" : "Popular Articles"}
            </h2>
            <div className="space-y-2">
              {featuredArticles
                .filter(a => a.slug !== "what-is-sorin")
                .slice(0, 5)
                .map((article, index) => {
                  const role = article.target_roles.includes("admin") ? "admin" 
                    : article.target_roles.includes("supervisor") ? "supervisor" 
                    : "worker";
                  const config = roleConfig[role];
                  
                  return (
                    <motion.button
                      key={article.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 + index * 0.05 }}
                      onClick={() => navigate(`/help/${article.slug}`)}
                      className={`w-full text-left p-4 rounded-2xl border hover:shadow-md transition-all group bg-gradient-to-r ${config.gradient} ${config.border}`}
                      whileHover={{ x: 4 }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${config.text} bg-background/60 backdrop-blur-sm`}>
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm group-hover:text-primary transition-colors truncate">
                            {language === "id" ? article.title_id : article.title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                            {language === "id" ? article.summary_id : article.summary}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                      </div>
                    </motion.button>
                  );
                })}
            </div>
          </motion.div>
        )}

        {/* Quick Links for Admin Features - Premium grid */}
        {!searchQuery && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <h2 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
              <Settings className="h-4 w-4" />
              {language === "id" ? "Fitur Lanjutan" : "Advanced Features"}
            </h2>
            <div className="grid grid-cols-3 gap-2">
              {[
                { icon: Brain, label: language === "id" ? "Psikometrik" : "Psychometrics", color: "from-violet-500/20 to-violet-500/5", iconColor: "text-violet-500", emoji: "🧠", categoryId: "advanced-psikometrik" },
                { icon: Network, label: language === "id" ? "Jaringan" : "Network", color: "from-cyan-500/20 to-cyan-500/5", iconColor: "text-cyan-500", emoji: "🌐", categoryId: "advanced-jaringan" },
                { icon: ClipboardCheck, label: language === "id" ? "Inspeksi" : "Inspections", color: "from-amber-500/20 to-amber-500/5", iconColor: "text-amber-500", emoji: "📋", categoryId: "advanced-inspeksi" },
                { icon: MapPin, label: language === "id" ? "Friction Map" : "Friction Map", color: "from-rose-500/20 to-rose-500/5", iconColor: "text-rose-500", emoji: "📍", categoryId: "advanced-friction" },
                { icon: AlertTriangle, label: language === "id" ? "Kelelahan" : "Fatigue", color: "from-orange-500/20 to-orange-500/5", iconColor: "text-orange-500", emoji: "😴", categoryId: "advanced-kelelahan" },
                { icon: BarChart3, label: language === "id" ? "Diagnostik" : "Diagnostics", color: "from-indigo-500/20 to-indigo-500/5", iconColor: "text-indigo-500", emoji: "📊", categoryId: "advanced-diagnostik" },
              ].map((item, i) => (
                <motion.button
                  key={item.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.35 + i * 0.05 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(`/help/category/${item.categoryId}`)}
                  className={`relative flex flex-col items-center gap-2 p-4 rounded-2xl bg-gradient-to-br ${item.color} border border-border/50 hover:border-border transition-all overflow-hidden group`}
                >
                  <motion.span 
                    className="absolute -right-1 -bottom-1 text-3xl opacity-20 group-hover:opacity-40"
                    animate={{ rotate: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {item.emoji}
                  </motion.span>
                  <item.icon className={`h-6 w-6 ${item.iconColor}`} />
                  <span className="text-xs font-medium text-center relative z-10">{item.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default HelpCenter;
